import { z } from 'zod';

/**
 * Canonical organization role vocabulary — the single source of truth shared by
 * client and server. Three tiers, highest → lowest:
 *
 * - `admin`  — full control: manage the team (invite, change roles, remove
 *   members), rename the organization, plus everything an editor can do. The
 *   user who creates an organization is its first admin.
 * - `editor` — "View and update": full access to the app's data surfaces
 *   (uploader, custom attributes, portfolio edits) but no team/org management.
 * - `viewer` — "View only": read-only. No uploader, no custom-attributes nav,
 *   read-only organization settings and team list.
 *
 * Privilege is a total order (admin > editor > viewer). `roleAtLeast` and the
 * helpers below are the only place ranking lives — never compare role strings
 * inline. Replaces the legacy owner/admin/member vocabulary.
 */
export const ORGANIZATION_ROLES = ['admin', 'editor', 'viewer'] as const;
export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

export const OrganizationRoleSchema = z.enum(ORGANIZATION_ROLES);

/** New invitees default to the least-privileged tier. */
export const DEFAULT_INVITE_ROLE: OrganizationRole = 'viewer';

/**
 * Roles offered when inviting a teammate. The invite form exposes only the two
 * non-admin tiers (admin is granted later via a role change), defaulting to
 * `viewer`.
 */
export const INVITE_ROLES = ['viewer', 'editor'] as const;
export const InviteRoleSchema = z.enum(INVITE_ROLES);
export type InviteRole = (typeof INVITE_ROLES)[number];

/**
 * Roles an admin may assign via a role change on the Team screen — any of the
 * three tiers ("change anyone's role to anything"). The server additionally
 * refuses changing your own role and demoting the last remaining admin.
 */
export const ASSIGNABLE_ROLES = ORGANIZATION_ROLES;
export const AssignableRoleSchema = OrganizationRoleSchema;

const ROLE_RANK: Record<OrganizationRole, number> = {
  admin: 3,
  editor: 2,
  viewer: 1,
};

/** Numeric privilege rank; unknown values rank 0 (no access). */
export function roleRank(role: OrganizationRole): number {
  return ROLE_RANK[role] ?? 0;
}

/** True iff `role` is at least as privileged as `min` (admin ⊇ editor ⊇ viewer). */
export function roleAtLeast(role: OrganizationRole, min: OrganizationRole): boolean {
  return roleRank(role) >= roleRank(min);
}

/** Team/organization management tier. */
export function isAdminRole(role: OrganizationRole): boolean {
  return role === 'admin';
}

/** Can mutate app data (uploader, custom attributes, portfolio): admin or editor. */
export function canEditData(role: OrganizationRole): boolean {
  return role === 'admin' || role === 'editor';
}

/** Human-facing labels for the role tiers (Team table, invite form, settings). */
export const ROLE_LABELS: Record<OrganizationRole, string> = {
  admin: 'Admin',
  editor: 'View and update',
  viewer: 'View only',
};

/** Short helper text describing each role, for the invite/role-change UI. */
export const ROLE_DESCRIPTIONS: Record<OrganizationRole, string> = {
  admin: 'Full access, including managing the team and organization settings.',
  editor: 'Can upload data and edit portfolio attributes.',
  viewer: 'Can view the portfolio but cannot upload or make changes.',
};
